import { StateCreator } from 'zustand';
import { BuildSlice } from '@/types/stores/build';
import { PlanSlice } from '@/types/stores/plan';
import { PlannerSlice } from '@/types/stores/planner';
import { ProjectSlice } from '@/types/stores/project';
import { RootActions } from '@/types/stores/rootActions';
import { generateHash, getLocale, getRandomString } from '@/common/utils';
import { SocketState } from '@/types/enums';
import i18n from '@/i18n';
import { airchefApi } from '@/api';
import { ValidationError } from '@/types/planner';
import { defaultMsGraphClient } from '@/api/MsGraphApiClient';
import { RoomId } from '@/api/airchef/Api';

// If browser locale is set to us, use us units by default
const isUs = getLocale() === 'en-US';

export const createPlannerSlice: StateCreator<
  BuildSlice & PlanSlice & PlannerSlice & ProjectSlice & RootActions,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  PlannerSlice
> = (set, get) => ({
  planner: {
    prefs: {
      showComments: true,
      showWallLabels: true,
      units: isUs ? 'us' : 'si',
      defaultWallHeight: isUs ? 3048 : 3000, // 10 ft or 3m
      // defaultWallHeight: isUs ? 1981.2 : 2000, // 2m or 6.5 ft
      preAuthPath: null,
      propertiesGroups: {},
      summary: {
        size: 'cozy',
        columns: {},
        configs: {},
      },
      warnings: {},
    },
    clipboard: {
      group: null,
    },
    currentRoom: '' as RoomId,
    gridSize: isUs ? 15.24 : 10,
    snapping: true,
    airchefVersion: 'unknown',
    pimVersion: 'unknown',
    language: i18n.resolvedLanguage ?? 'en',
    langLoading: false,
    mouseReservation: '',
    conversionsLoading: false,
    imageRendering: false,
    prefActions: {
      toggleDisplayColumn(type, column) {
        set(
          (state) => {
            if (!state.planner.prefs.summary.columns[type]) {
              state.planner.prefs.summary.columns[type] = [];
            }
            if (state.planner.prefs.summary.columns[type].includes(column)) {
              state.planner.prefs.summary.columns[type] = state.planner.prefs.summary.columns[type].filter(
                (c) => c !== column,
              );
            } else {
              state.planner.prefs.summary.columns[type].push(column);
            }
          },
          undefined,
          { type: 'planner/prefActions/toggleDisplayColumn', item: type, column },
        );
      },
      initColumns(type, columns, force = false) {
        set(
          (state) => {
            if (
              !state.planner.prefs.summary.columns[type] ||
              state.planner.prefs.summary.columns[type].length === 0 ||
              force
            ) {
              state.planner.prefs.summary.columns[type] = columns;
            }
          },
          undefined,
          { type: 'planner/prefActions/initColumns', item: type, columns },
        );
      },
      async validateAndInitColumns(config) {
        const type = config.itemType;

        const oldConfigHash = get().planner.prefs.summary.configs[type];

        const configHash = await generateHash(JSON.stringify(config));

        if (!oldConfigHash || oldConfigHash !== configHash) {
          get().planner.prefActions.initColumns(config.itemType, config.defaultColumns, true);
          set(
            (state) => {
              state.planner.prefs.summary.configs[type] = configHash;
            },
            undefined,
            { type: 'planner/prefActions/validateAndInitColumns' },
          );
        }
      },
      changeSummarySize: (size) => {
        set(
          (state) => {
            state.planner.prefs.summary.size = size;
          },
          undefined,
          { type: 'planner/prefActions/changeSummarySize', size },
        );
      },
      togglePropertiesGroup: (type, group, open) => {
        set(
          (state) => {
            if (!state.planner.prefs.propertiesGroups[type]) {
              state.planner.prefs.propertiesGroups[type] = {};
            }
            if (!state.planner.prefs.propertiesGroups[type][group]) {
              state.planner.prefs.propertiesGroups[type][group] = { open: true };
            }

            const currentState = state.planner.prefs.propertiesGroups[type][group].open;
            const newState = open ?? !currentState;

            state.planner.prefs.propertiesGroups[type][group] = { open: newState };
          },
          undefined,
          { type: 'planner/prefActions/togglePropertiesGroup', assetType: type, group, open },
        );
      },
      setLastSeenWarning(timestamp) {
        const { id: projectId } = get().project.data;
        if (!projectId) return;

        set(
          (state) => {
            state.planner.prefs.warnings[projectId] = timestamp;
          },
          undefined,
          { type: 'planner/prefActionssetLastSeenWarning', timestamp },
        );
      },
    },
    setGridSize: (size) => {
      set(
        (state) => {
          state.planner.gridSize = size;
        },
        undefined,
        { type: 'planner/setGridSize', size },
      );
    },
    setSnapping: (snapping) => {
      set(
        (state) => {
          state.planner.snapping = snapping;
        },
        undefined,
        { type: 'planner/setSnapping', snapping },
      );
    },
    toggleSnapping: () => {
      set(
        (state) => {
          state.planner.snapping = !state.planner.snapping;
        },
        undefined,
        { type: 'planner/toggleSnapping' },
      );
    },
    setDefaultWallheight: (height) => {
      set(
        (state) => {
          state.planner.prefs.defaultWallHeight = height;
        },
        undefined,
        { type: 'planner/setDefaultWallheight', height },
      );
    },
    setShowComments(show) {
      set(
        (state) => {
          state.planner.prefs.showComments = show;
        },
        undefined,
        { type: 'planner/setShowComments', show },
      );
    },
    setShowWallLabels(show) {
      set(
        (state) => {
          state.planner.prefs.showWallLabels = show;
        },
        undefined,
        { type: 'planner/setShowWallLabels', show },
      );
    },
    setUnits: (units) => {
      set(
        (state) => {
          state.planner.prefs.units = units;
          state.planner.gridSize = units === 'si' ? 10 : 15.24;
        },
        undefined,
        { type: 'planner/setUnits', units },
      );
    },

    changeLanguage: (lang, storeOnly = false) => {
      set(
        (state) => {
          state.planner.language = lang;
          state.planner.langLoading = !storeOnly;
        },
        undefined,
        { type: 'planner/changeLanguage', storeOnly },
      );
      if (!storeOnly) {
        i18n.changeLanguage(lang).finally(() => {
          set(
            (state) => {
              state.planner.langLoading = false;
            },
            undefined,
            { type: 'planner/changeLanguage/loading', loading: false },
          );
        });
      }
    },
    setReserved: (source) => {
      if (!source || source === '') {
        return false;
      }
      const currentReservation = get().planner.mouseReservation;
      if (currentReservation !== '' && currentReservation !== source) {
        return false;
      }
      set(
        (state) => {
          state.planner.mouseReservation = source;
        },
        undefined,
        { type: 'planner/setReserved', source },
      );
      return true;
    },
    clearReserved: (source) => {
      if (!source || source === '') {
        return true;
      }
      const currentReservation = get().planner.mouseReservation;
      if (!currentReservation || currentReservation === '') {
        return true;
      }

      if (currentReservation !== source) {
        return false;
      }

      set(
        (state) => {
          state.planner.mouseReservation = '';
        },
        undefined,
        { type: 'planner/clearReserved', source },
      );
      return true;
    },
    setConversionsLoading: (loading) => {
      set(
        (state) => {
          state.planner.conversionsLoading = loading;
        },
        undefined,
        { type: 'planner/setConversionsLoading', loading },
      );
    },
    setPreAuthPath: (path) => {
      set(
        (state) => {
          state.planner.prefs.preAuthPath = path ?? null;
        },
        undefined,
        { type: 'planner/setPreAuthPath' },
      );
    },
    setServiceVersion(service, version) {
      const versionProp = `${service}Version` as const;
      const currentVersion = get().planner[versionProp];

      if (currentVersion === version) return;
      set(
        (state) => {
          state.planner[versionProp] = version;
        },
        undefined,
        { type: 'planner/setServiceVersion', service, version },
      );
    },
    clear: () => {
      set(
        (state) => {
          state.planner.currentRoom = '' as RoomId;
          state.planner.mouseReservation = '';
          state.planner.validationErrors.list = [];
          state.planner.prefs.preAuthPath = null;
        },
        undefined,
        { type: 'planner/clear' },
      );
    },
    validationErrors: {
      list: [],
      add: (...paths) => {
        const ve: ValidationError = {
          id: getRandomString(4),
          paths,
        };
        set(
          (state) => {
            state.planner.validationErrors.list.push(ve);
          },
          undefined,
          { type: 'planner/validationErrors/add', paths },
        );
      },
      dismiss: (id) => {
        set(
          (state) => {
            state.planner.validationErrors.list = state.planner.validationErrors.list.filter((ve) => ve.id !== id);
          },
          undefined,
          { type: 'planner/validationErrors/dismiss', id },
        );
      },
    },
    projectTemplates: {
      map: {},
      load: async () => {
        try {
          const response = await airchefApi.project.templatesList();
          set(
            (state) => {
              for (const ptype of response.data.types ?? []) {
                if (ptype && response.data?.projectTemplatesByType?.[ptype]) {
                  // @ts-expect-error The ui has more strict type overrides for certain haltonvalues types
                  state.planner.projectTemplates.map[ptype] = response.data.projectTemplatesByType[ptype];
                }
              }
            },
            undefined,
            { type: 'planner/projectTemplates/load' },
          );

          return response.data;
        } catch (err) {
          console.log('Failed to load project templates', err);
          throw err;
        }
      },
    },
    user: {
      id: '',
      data: null,
      load: async () => {
        const user = await defaultMsGraphClient.getUserInfo();
        const photo = await defaultMsGraphClient.getUserPhoto();
        set(
          (state) => {
            state.planner.user.id = user.id;
            state.planner.user.data = { ...user, photo };
          },
          undefined,
          { type: 'planner/user/load' },
        );
      },
      clear: () => {
        set(
          (state) => {
            state.planner.user.id = '';
            state.planner.user.data = null;
          },
          undefined,
          { type: 'planner/user/clear' },
        );
      },
    },
    socket: {
      socketState: SocketState.DISCONNECTED,
      hasErrors: false,
      getSocketState() {
        return get().planner.socket.socketState;
      },
      setSocketState: (socketState) => {
        if (typeof socketState === 'function') {
          const nextValue = socketState(get().planner.socket.socketState);
          if (!nextValue) return;
          socketState = nextValue;
        }

        if (get().planner.socket.socketState === socketState) return;
        set(
          (state) => {
            state.planner.socket.socketState = socketState;
          },
          undefined,
          { type: 'planner/socket/setSocketState', socketState },
        );
      },
      setHasErrors: (errors) => {
        if (get().planner.socket.hasErrors === errors) return;
        set(
          (state) => {
            state.planner.socket.hasErrors = !!errors;
          },
          undefined,
          { type: 'planner/socket/setHasErrors', errors },
        );
      },
    },
    copyGroup: (groupId) => {
      set(
        (state) => {
          state.planner.clipboard.group = groupId ?? null;
        },
        undefined,
        { type: 'planner/copyGroup', groupId },
      );
    },
    setImageRendering: (rendering) => {
      set(
        (state) => {
          state.planner.imageRendering = rendering;
        },
        undefined,
        { type: 'planner/setImageRendering', rendering },
      );
    },
  },
});
